'use client';
import { useEffect, useState } from 'react';

import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { useScroll } from '@hooks/hooks';
import { SearchViewModel } from '@api/v3';
import { useDevice } from '@contexts/device';
import SearchReservedId from '@components/ui/SearchReservedId';
import Typography from '@components/Typography';

const SearchBar = dynamic(() => import('../components/SearchBar/SearchBar'), {
  ssr: true,
});

const Image = dynamic(() => import('next/image'));

const StickySearchWrapper = styled.div<{
  sticky?: boolean;
}>`
  margin: 0 auto;
  ${({ sticky }) => `
      margin-top: 0;
      padding: ${sticky ? '10px 0' : '0'};
      position: ${sticky ? 'fixed' : 'unset'};
      width: ${sticky ? '100vw' : 'fit-content'};
      `}
  z-index: 999;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;

  transition: all 0.35s;

  background-color: ${({ sticky, theme }) =>
    sticky ? theme.palette.white : 'transparent'};
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    margin-top: 0;
  }
`;

const StickySearchBarContentWrapper = styled.div<{
  sticky?: boolean;
}>`
  display: flex;
  justify-content: center;
  margin: 0 auto;

  ${({ sticky }) => ` ${
    sticky
      ? `
      display: grid;
    grid-template-columns: 280px 960px;
    `
      : ''
  }
      `}
`;

const SearchBarWrapper = styled.div`
  max-width: 100%;

  & > div {
    max-width: 100%;
    width: 100%;
  }

  @media ${({ theme }) => `(min-width: ${theme.breakPoint.LARGE_1X})`} {
    max-width: fit-content;

    & > div {
      max-width: 960px;
      width: 960px;
    }
  }
`;

const StickyLogoBox = styled.div`
  display: flex;
  align-items: center;
  margin-right: auto;
  margin-left: auto;
  @media only screen and (max-width: 1100px) {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  img {
    width: 22px;
    height: 30px;
  }
`;

const StickyText = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.palette.coral};
  margin-left: 20px;
`;

export const KocAilemContainer = styled.div`
  max-width: 1240px;
  width: 100%;
  margin: auto;
`;
interface TourListingSearchBarProps {
  tourListCount: number;
  type?: 'hotel' | 'tour' | 'ship';
  handleSearch?: () => void;
  detailQueryKey?: string;
  tourStyle?: boolean;
  tourListing?: boolean;
  searchText?: string;
  shouldShallowPush?: boolean;
  destinationSearchText?: string;
  destination?: SearchViewModel;
  activeDestinationText?: string;
  onDestinationTextChange: (s: string) => void;
  onDestinationChange: (d: SearchViewModel) => void;
  errors: FieldErrors<{ destination: string }>;
  setIsRedirecting?: (isRedirecting: boolean) => void;
  isSegmentPage?: boolean;
}

const TourListingSearchBar = ({
  tourListCount,
  type,
  handleSearch,
  destination,
  activeDestinationText,
  destinationSearchText,
  onDestinationChange,
  onDestinationTextChange,
  shouldShallowPush,
  errors,
  setIsRedirecting,
  isSegmentPage,
}: TourListingSearchBarProps) => {
  const { isDesktop } = useDevice();

  const scroll = useScroll();

  const [isStickySearch, setIsStickyHeader] = useState<boolean>(false);

  useEffect(() => {
    if (scroll.y && scroll.y > 200) {
      setIsStickyHeader(true);
    } else {
      setIsStickyHeader(false);
    }
  }, [scroll.y]);

  return isDesktop ? (
    <>
      <StickySearchWrapper sticky={isStickySearch}>
        <SearchReservedId show={isStickySearch} />
        <StickySearchBarContentWrapper sticky={isStickySearch}>
          {isStickySearch ? (
            <StickyLogoBox>
              <Link href='/' prefetch={false}>
                <Image
                  alt='setur logo'
                  src='/static/images/misc-setur-logo-s.svg'
                  width={22}
                  height={30}
                />
              </Link>

              <StickyText>
                <Typography variant='Title5' color='BR300' weight='SemiBold'>
                  {activeDestinationText?.replace('-', ' ')}
                </Typography>
              </StickyText>
            </StickyLogoBox>
          ) : null}

          <SearchBarWrapper>
            <SearchBar
              setIsRedirecting={setIsRedirecting}
              tourListing={true}
              handleSearch={handleSearch}
              tourListCount={tourListCount || 0}
              type={type}
              shouldShallowPush={shouldShallowPush}
              destination={destination}
              onDestinationChange={onDestinationChange}
              onDestinationTextChange={onDestinationTextChange}
              destinationSearchText={destinationSearchText}
              errors={errors}
            />
          </SearchBarWrapper>
        </StickySearchBarContentWrapper>
      </StickySearchWrapper>
    </>
  ) : (
    <SearchBarWrapper>
      <SearchBar
        setIsRedirecting={setIsRedirecting}
        tourListing={true}
        handleSearch={handleSearch}
        tourListCount={tourListCount || 0}
        type={type}
        shouldShallowPush={shouldShallowPush}
        destination={destination}
        onDestinationChange={onDestinationChange}
        onDestinationTextChange={onDestinationTextChange}
        destinationSearchText={destinationSearchText}
        errors={errors}
        sticky={isStickySearch}
        isSegmentPage={isSegmentPage}
      />
    </SearchBarWrapper>
  );
};

export default TourListingSearchBar;
